<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import watermarkUrl from '@/assets/logo-watermark.svg'
import { useAuth } from '@/composables/useAuth'
import { accountHref } from '@/router/products'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'

const { t } = useI18n()
const route = useRoute()
const { isAuthenticated } = useAuth()

const currentLocale = computed<Locale>(() => route.meta.locale ?? DEFAULT_LOCALE)

/** Avoids a hydration mismatch: the session is unknown at pre-render time, so
 *  the authenticated variant of the contact link is only used once mounted. */
const mounted = ref(false)
onMounted(() => { mounted.value = true })

/** « Contacts et aide »: internal form when signed in, client website otherwise
 *  (same rule as the top bar and the public header). */
const EXTERNAL_CONTACT = 'https://smerra.fr/contacts/'
const contactInternal = computed(() => mounted.value && isAuthenticated.value)
const contactHref = computed(() =>
  contactInternal.value ? accountHref('contact', currentLocale.value) : EXTERNAL_CONTACT,
)

/** Client website sections, in the order given by the design. `contact` is the
 *  only entry that can point inside the app (see `contactHref`); the others are
 *  absolute URLs on smerra.fr — housing lives on its own subdomain.
 *  « Smerra & toi » and « Prévention santé » share /nos-actions/ on purpose:
 *  the client site itself maps both entries to that page. */
const siteLinks = computed(() => [
  { key: 'footer.about',        href: 'https://smerra.fr/a-propos/',    external: true },
  { key: 'footer.smerraAndYou', href: 'https://smerra.fr/nos-actions/', external: true },
  { key: 'topbar.contact',      href: contactHref.value,                external: !contactInternal.value },
  { key: 'footer.prevention',   href: 'https://smerra.fr/nos-actions/', external: true },
  { key: 'footer.housing',      href: 'https://logement.smerra.fr/',    external: true },
])

/** Réseaux sociaux : tous en target=_blank rel=noopener.
 *  L'icône SVG est gardée inline pour éviter une dépendance icônes externe.
 *  `viewBox` est porté par chaque entrée : le glyphe Instagram vient du site
 *  client, dessiné sur une grille 22 plutôt que 24. */
const socials = [
  {
    label: 'Facebook',
    viewBox: '0 0 24 24',
    href: 'https://www.facebook.com/smerra.fr',
    path: 'M22 12a10 10 0 1 0-11.6 9.9v-7H8v-2.9h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.4h-1.2c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0 0 22 12Z',
  },
  {
    label: 'Instagram',
    viewBox: '0 0 22 22',
    href: 'https://www.instagram.com/la_smerra/',
    path: 'M7.333 2.75A4.584 4.584 0 0 0 2.75 7.333v7.334a4.584 4.584 0 0 0 4.583 4.583h7.334a4.584 4.584 0 0 0 4.583-4.583V7.333a4.584 4.584 0 0 0-4.583-4.583H7.333ZM16.5 4.583a.917.917 0 1 1 0 1.834.917.917 0 0 1 0-1.834ZM11 6.417a4.584 4.584 0 1 1-.001 9.167A4.584 4.584 0 0 1 11 6.417Zm0 1.833a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5Z',
  },
  {
    label: 'YouTube',
    viewBox: '0 0 24 24',
    href: 'https://www.youtube.com/@smerra_etudiants',
    path: 'M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2C2 8.8 2 12 2 12s0 3.2.4 4.8a2.5 2.5 0 0 0 1.8 1.8c1.6.4 7.8.4 7.8.4s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8c.4-1.6.4-4.8.4-4.8s0-3.2-.4-4.8ZM10 15.2V8.8L15.5 12 10 15.2Z',
  },
  {
    label: 'LinkedIn',
    viewBox: '0 0 24 24',
    href: 'https://www.linkedin.com/company/smerra/',
    path: 'M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2ZM8.5 18H6V10h2.5v8ZM7.2 8.9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3ZM18 18h-2.5v-4.3c0-1-.4-1.7-1.3-1.7-.8 0-1.2.5-1.4 1V18H10.3V10h2.4v1c.4-.5 1-1.2 2.4-1.2 1.7 0 3 1.2 3 3.7V18Z',
  },
] as const

/** Liens légaux — externes, tous en target=_blank rel=noopener.
 *  Label via i18n, URL absolue (smerra.fr / Typeform pour la réclamation). */
const legalLinks = [
  { key: 'complaint',   href: 'https://uitsem1.typeform.com/to/I6sBuXr6?typeform-source=smerra.fr' },
  { key: 'legalNotice', href: 'https://smerra.fr/mentions-legales/' },
  { key: 'privacy',     href: 'https://smerra.fr/politique-confidentialite-cookies/' },
] as const

const year = new Date().getFullYear()
</script>

<template>
  <footer class="footer">
    <div class="container">
      <nav class="footer__site" :aria-label="t('footer.siteLabel')">
        <ul>
          <li v-for="l in siteLinks" :key="l.key">
            <a v-if="l.external" :href="l.href" target="_blank" rel="noopener">{{ t(l.key) }}</a>
            <router-link v-else :to="l.href">{{ t(l.key) }}</router-link>
          </li>
        </ul>
      </nav>
    </div>

    <div class="container footer__inner">
      <ul class="footer__socials" :aria-label="t('footer.socialsLabel')">
        <li v-for="s in socials" :key="s.label">
          <a :href="s.href" :aria-label="s.label" target="_blank" rel="noopener">
            <svg :viewBox="s.viewBox" width="22" height="22" aria-hidden="true">
              <path :d="s.path" fill="currentColor" />
            </svg>
          </a>
        </li>
      </ul>

      <nav class="footer__legal" :aria-label="t('footer.legalLabel')">
        <ul>
          <li v-for="l in legalLinks" :key="l.key">
            <a :href="l.href" target="_blank" rel="noopener">{{ t(`footer.${l.key}`) }}</a>
          </li>
        </ul>
      </nav>

      <p class="footer__copy">MySmerra {{ year }}®</p>
    </div>

    <img
      class="footer__watermark"
      :src="watermarkUrl"
      alt=""
      aria-hidden="true"
      decoding="async"
    />
  </footer>
</template>

<style scoped>
.footer {
  position: relative;
  background: #ebe5e9;
  color: var(--color-chocolate);
  padding-top: 3.5rem;
  overflow: hidden;
}
@media (min-width: 64rem) {
  .footer { padding-top: 6.25rem; }
}

/* Site nav — rounded white panel above the utility row. It spans the layout
   container (inset from the viewport edges, never a full-bleed white band). */
.footer__site {
  background: var(--color-white);
  border-radius: var(--radius-lg);
  padding: var(--space-5) var(--space-4);
  margin-bottom: var(--space-10);
}
@media (min-width: 64rem) {
  .footer__site {
    padding: var(--space-5) clamp(1.5rem, 5.5vw, 5.5rem);
    margin-bottom: var(--space-12);
  }
}
.footer__site ul {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
}
@media (min-width: 48rem) {
  .footer__site ul {
    flex-direction: row;
    gap: var(--space-2);
  }
  /* Equal-width cells so the labels read as evenly spaced whatever their length */
  .footer__site li { flex: 1; }
}
.footer__site a {
  display: block;
  text-align: center;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  transition: color var(--dur-fast) var(--ease-out);
}
.footer__site a:hover,
.footer__site a:focus-visible { color: var(--color-cherry); }

.footer__inner {
  display: flex;
  gap: var(--space-8);
  font-size: var(--text-xs);
  align-items: center;
  flex-direction: column;
  text-align: center;
}
@media (min-width: 64rem) {
  .footer__inner {
    flex-direction: row;
    text-align: left;
  }
}

/* Socials */
.footer__socials {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.footer__socials a {
  display: inline-flex;
  color: var(--color-chocolate);
  transition: color var(--dur-fast) var(--ease-out);
}
.footer__socials a:hover { color: var(--color-cherry); }

/* Legal nav */
.footer__legal {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
@media (min-width: 64rem) {
  .footer__legal { margin-left: auto; }
}
.footer__legal ul {
  display: flex;
  gap: var(--space-6);
  flex-direction: column;
  align-items: center;
}
@media (min-width: 64rem) {
  .footer__legal ul { flex-direction: row; align-items: center; }
}
.footer__legal a {
  display: block;
  font-weight: 700;
  color: var(--color-gray-800);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  transition: color var(--dur-fast) var(--ease-out);
}
.footer__legal a:hover { color: var(--color-chocolate); }

.footer__copy {
  font-weight: 500;
}

/* Watermark logo bleeding at the bottom */
.footer__watermark {
  display: block;
  width: 100%;
  height: auto;
  margin-top: 2.75rem;
  margin-bottom: -0.5rem;
  opacity: 0.1;
  pointer-events: none;
  user-select: none;
}
@media (min-width: 48rem) {
  .footer__watermark { margin-bottom: -1rem; }
}
@media (min-width: 64rem) {
  .footer__watermark { margin-bottom: -2.5rem; }
}
</style>
